import React, { useEffect, useMemo, useState } from 'react';
import '@brands/Dashboard/MultiStage/multiStage.scss';

import { addOneReportObject } from '@brands/services/oneReport/addOneReportObject';
import { deleteOneReportObject } from '@brands/services/oneReport/deleteOneReportObject';
import { ObjectType, updateOneReportObject } from '@brands/services/oneReport/updateOneReportObject';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { ICase } from '@brands/services/cases/types/ICase';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { FieldErrors } from 'react-hook-form';
import { OralHealthReportSubmit } from '@brands/Dashboard/ConsultationPage/assets/types';

type Props = {
  thisCase: ICase;
  setCase: React.Dispatch<React.SetStateAction<any>>;
  setEditingFulfillmentApproval: React.Dispatch<React.SetStateAction<boolean | null>>;
  errors: FieldErrors<OralHealthReportSubmit>;
  disabled?: boolean;
};

// DO-3882: provider's fulfillment decision (Yes/No + required notes). Saves as a
// one-report fulfillment_approval object; surfaced in the OHR and drives the
// patient fulfillment notification.
const FulfillmentApprovalCard = ({
  thisCase,
  setCase,
  setEditingFulfillmentApproval,
  errors,
  disabled = false,
}: Props): JSX.Element => {
  const existing = thisCase.one_report?.fulfillment_approval || null;
  const [approved, setApproved] = useState<boolean | null>(existing ? existing.approved : null);
  const [notes, setNotes] = useState<string>(existing?.notes || '');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const savedState = useMemo(
    () => ({
      approved: existing ? existing.approved : null,
      notes: existing?.notes || '',
    }),
    [existing?.approved, existing?.notes],
  );

  useEffect(() => {
    setApproved(savedState.approved);
    setNotes(savedState.notes);
    setError('');
  }, [savedState.approved, savedState.notes]);

  useEffect(() => {
    if (errors._fulfillment_approval_must_be_saved) {
      setError(errors._fulfillment_approval_must_be_saved.message || 'Fulfillment approval must be saved before proceeding.');
    }
  }, [errors]);

  const hasSelection = approved !== null;
  const isDirty = approved !== savedState.approved || (hasSelection && notes !== savedState.notes);
  const locked = disabled || saving;
  const canClearSavedSelection = Boolean(existing && approved === null);
  const canSave = isDirty && (canClearSavedSelection || (hasSelection && notes.trim() !== ''));

  useEffect(() => {
    setEditingFulfillmentApproval(approved !== savedState.approved || (hasSelection && notes !== savedState.notes));
  }, [approved, hasSelection, notes, savedState.approved, savedState.notes]);

  const handleDecisionClick = (value: boolean): void => {
    if (locked) return;

    setApproved((current) => (current === value ? null : value));
    setError('');
  };

  const clearSelection = (): void => {
    if (locked) return;

    setApproved(null);
    setError('');
  };

  const handleSave = async (): Promise<void> => {
    if (!isDirty) {
      return;
    }
    if (approved === null && !existing) {
      return;
    }
    if (approved !== null && !notes.trim()) {
      setError('Additional information is required.');
      return;
    }
    const oneReportId = thisCase.one_report?.id?.toString();
    if (!oneReportId) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (approved === null && existing) {
        await deleteOneReportObject(oneReportId, existing.id.toString(), ObjectType.FulfillmentApproval);
      } else if (approved !== null && existing) {
        await updateOneReportObject(oneReportId, ObjectType.FulfillmentApproval, {
          id: existing.id.toString(),
          approved,
          notes,
        });
      } else if (approved !== null) {
        await addOneReportObject(oneReportId, ObjectType.FulfillmentApproval, { approved, notes });
      }
      const refreshed = await getOneCase(Number(thisCase.id));
      setCase(refreshed);
    } catch (e) {
      displayErrorDetails(e as any);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ms-scope" style={{ marginTop: 16 }}>
      <div className={`ms-card ${error !== '' ? 'ms-error-container' : ''}`}>
        <h3 className="ms-title">Fulfillment approval</h3>
        <p className="ms-desc">
          Use this section to indicate whether fulfillment is approved for this case. Please note that if &quot;No&quot;
          is selected, fulfillment will be declined and the case will be closed.
        </p>

        <div className="ms-yes-no" style={{ marginBottom: 16 }}>
          <button
            type="button"
            className={approved === true ? 'is-on' : ''}
            disabled={locked}
            aria-pressed={approved === true}
            onClick={() => handleDecisionClick(true)}
          >
            Yes
          </button>
          <button
            type="button"
            className={approved === false ? 'is-on' : ''}
            disabled={locked}
            aria-pressed={approved === false}
            onClick={() => handleDecisionClick(false)}
          >
            No
          </button>
        </div>

        {hasSelection && !disabled && (
          <button
            type="button"
            className="ms-cancel-link"
            style={{ width: 'auto', marginTop: -8, marginBottom: 12, padding: 0 }}
            disabled={locked}
            onClick={clearSelection}
          >
            Clear selection
          </button>
        )}

        {hasSelection && (
          <>
            {approved === false && (
              <p className="ms-error" style={{ marginBottom: 8 }}>
                Selecting &quot;No&quot; will inform the patient that they are being declined the product/service.
              </p>
            )}
            <label className="ms-field-label">
              Additional information<span className="ms-req">*</span>
            </label>
            <textarea
              className="ms-field-textarea"
              rows={3}
              value={notes}
              disabled={locked}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Provide details about the fulfillment decision"
            />
          </>
        )}

        {error !== '' && <p className="ms-error">{error}</p>}

        {!disabled && hasSelection && (
          <button
            type="button"
            className="ms-primary-btn"
            style={{ marginTop: 16, width: 'auto', minWidth: 220 }}
            disabled={!canSave || saving}
            onClick={handleSave}
          >
            {saving ? 'Saving…' : 'Save fulfillment decision'}
          </button>
        )}
      </div>
    </div>
  );
};

export default FulfillmentApprovalCard;
